import React from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import type { PipelineAgingAnalysisTableProps } from "../../type";
import { EMPTY_PIPELINE_AGING_ANALYSIS_MESSAGE } from "../../../../utils/consts";
import {
  getPipelineAgingAnalysisTitleSx,
  pipelineAgingAnalysisConfig,
  pipelineAgingAnalysisStyles,
} from "./styles/pipelineAgingAnalysisStyles";

const formatPipelineValue = (valueInDollars: number): string => {
  const millions = valueInDollars / 1_000_000;
  return `$${millions.toLocaleString("en-US", {
    maximumFractionDigits: 2,
  })}m`;
};

const formatDistribution = (distribution: number): string =>
  `${distribution.toFixed(2)}%`;

export const PipelineAgingAnalysisTable: React.FC<
  PipelineAgingAnalysisTableProps
> = ({
  title,
  loading = false,
  rows,
  titleAlign = pipelineAgingAnalysisConfig.titleAlign,
  emptyMessage = EMPTY_PIPELINE_AGING_ANALYSIS_MESSAGE,
}) => {
  const hasData = rows.length > 0;

  if (!hasData && !loading) {
    return (
      <Box sx={pipelineAgingAnalysisStyles.root}>
        {title ? (
          <Typography sx={getPipelineAgingAnalysisTitleSx(titleAlign)}>
            {title}
          </Typography>
        ) : null}
        <Box sx={pipelineAgingAnalysisStyles.emptyStateContainer}>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            {emptyMessage}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={pipelineAgingAnalysisStyles.root}>
      {title ? (
        <Typography sx={getPipelineAgingAnalysisTitleSx(titleAlign)}>
          {title}
        </Typography>
      ) : null}
      {hasData && !loading && (
        <Box sx={pipelineAgingAnalysisStyles.tableWrapper(loading)}>
          <Table sx={pipelineAgingAnalysisStyles.table}>
            <TableHead>
              <TableRow sx={pipelineAgingAnalysisStyles.headerRow}>
                <TableCell
                  sx={pipelineAgingAnalysisStyles.headerCell}
                  align="left"
                >
                  Ageing Bucket
                </TableCell>
                <TableCell
                  sx={pipelineAgingAnalysisStyles.headerCell}
                  align="left"
                >
                  Wt Pipeline ($m)
                </TableCell>
                <TableCell
                  sx={pipelineAgingAnalysisStyles.headerCell}
                  align="left"
                >
                  Distribution (%)
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow
                  key={row.bucket}
                  sx={pipelineAgingAnalysisStyles.bodyRow}
                >
                  <TableCell
                    sx={{
                      ...pipelineAgingAnalysisStyles.bodyCell,
                      ...pipelineAgingAnalysisStyles.bodyCellLeft,
                      ...pipelineAgingAnalysisStyles.firstColumnBodyCell,
                    }}
                    align="left"
                  >
                    <Typography
                      component="span"
                      sx={pipelineAgingAnalysisStyles.bucketLink}
                    >
                      {row.bucket}
                    </Typography>
                  </TableCell>
                  <TableCell
                    sx={{
                      ...pipelineAgingAnalysisStyles.bodyCell,
                      ...pipelineAgingAnalysisStyles.bodyCellCenter,
                      ...pipelineAgingAnalysisStyles.pipelineValue,
                    }}
                    align="center"
                  >
                    {formatPipelineValue(row.weightedPipeline)}
                  </TableCell>
                  <TableCell
                    sx={{
                      ...pipelineAgingAnalysisStyles.bodyCell,
                      ...pipelineAgingAnalysisStyles.bodyCellLeft,
                    }}
                    align="left"
                  >
                    <Box
                      sx={pipelineAgingAnalysisStyles.distributionBarContainer}
                    >
                      <Box
                        sx={pipelineAgingAnalysisStyles.distributionBarTrack}
                      >
                        <Tooltip
                          title={formatDistribution(row.distribution)}
                          arrow
                          placement="bottom"
                        >
                          <Box
                            sx={pipelineAgingAnalysisStyles.distributionBar(
                              row.distribution,
                            )}
                          />
                        </Tooltip>
                      </Box>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </Box>
  );
};
